import React, { useEffect, useState, useRef } from "react";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import { ConnectPayouts, ConnectAccountManagement, ConnectComponentsProvider } from "@stripe/react-connect-js";
import { Loader2 } from "lucide-react";
import api from "../lib/api";

/**
 * Inline embedded Connect components for an onboarded affiliate.
 * Renders Stripe's payouts list + account-management screens inside our app.
 */
export default function ConnectInlinePanels() {
  const [stripeConnect, setStripeConnect] = useState(null);
  const [loading, setLoading] = useState(true);
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    (async () => {
      try {
        const cfg = await api.connectPublishableKey();
        if (!cfg.configured) {
          setLoading(false);
          return;
        }
        const sc = await loadConnectAndInitialize({
          publishableKey: cfg.publishable_key,
          fetchClientSecret: async () => {
            const r = await api.connectOnboard();
            return r.client_secret;
          },
          appearance: {
            overlays: "dialog",
            variables: {
              colorPrimary: "#0A0A0B",
              colorBackground: "#FFFFFF",
              fontFamily: "Inter, system-ui, sans-serif",
              borderRadius: "0px",
            },
          },
        });
        setStripeConnect(sc);
      } catch (e) {
        console.error("Connect inline init failed", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="border border-[#E2E4E9] p-6 text-sm text-[#52525B] flex items-center gap-2">
        <Loader2 size={14} className="animate-spin"/> Loading your Stripe dashboard…
      </div>
    );
  }
  if (!stripeConnect) return null;

  return (
    <div className="space-y-5" data-testid="connect-inline-panels">
      <div className="border-2 border-black p-5 bg-white">
        <div className="text-eyebrow mb-3">Payouts</div>
        <ConnectComponentsProvider connectInstance={stripeConnect}>
          <ConnectPayouts />
        </ConnectComponentsProvider>
      </div>
      <div className="border-2 border-black p-5 bg-white">
        <div className="text-eyebrow mb-3">Account & bank settings</div>
        <ConnectComponentsProvider connectInstance={stripeConnect}>
          <ConnectAccountManagement />
        </ConnectComponentsProvider>
      </div>
    </div>
  );
}
